import type { PhoneCallStatistic, TaskSessionStatistic, TaskStatistics } from '@frontend/api-types';
import { EArray } from '@frontend/duck-tape';

export type TaskStatisticsBarSegment = {
  accentSeconds?: number;
  key: string;
  seconds: number;
};

type TaskStatisticsElapsedItem = {
  elapsedTimeSeconds: MaybeNull<number>;
};

export const getTaskStatisticsElapsedSeconds = <T extends TaskStatisticsElapsedItem>(item: T) =>
  item.elapsedTimeSeconds ?? 0;

export const getSafeTaskStatisticsPhoneCalls = (taskStatistics: TaskStatistics): PhoneCallStatistic[] =>
  Array.isArray(taskStatistics.phoneCalls) ? taskStatistics.phoneCalls : [];

export const getSafeTaskStatisticsSessions = (taskStatistics: TaskStatistics): TaskSessionStatistic[] =>
  Array.isArray(taskStatistics.taskSessions) ? taskStatistics.taskSessions : [];

export const sumTaskStatisticsElapsedSeconds = <T extends TaskStatisticsElapsedItem>(items: T[]) =>
  items.reduce((total, item) => total + getTaskStatisticsElapsedSeconds(item), 0);

export const sortTaskStatisticsItemsByElapsedSecondsDescending = <T extends TaskStatisticsElapsedItem>(
  items: T[],
): T[] =>
  EArray.sort(
    items.map((item, index) => ({ index, item })),
    (left: { index: number; item: T }, right: { index: number; item: T }) => {
      const elapsedDiff = getTaskStatisticsElapsedSeconds(right.item) - getTaskStatisticsElapsedSeconds(left.item);

      return elapsedDiff > 0
        ? 1
        : elapsedDiff < 0
          ? -1
          : left.index > right.index
            ? 1
            : left.index < right.index
              ? -1
              : 0;
    },
  ).map(({ item }) => item);

export const getTaskStatisticsSessionCount = (taskStatistics: TaskStatistics) =>
  getSafeTaskStatisticsSessions(taskStatistics).length;

export const getTaskStatisticsCallCount = (taskStatistics: TaskStatistics) =>
  getSafeTaskStatisticsPhoneCalls(taskStatistics).length;

export const getTaskStatisticsDisplayCallCount = (taskStatistics: TaskStatistics) =>
  Math.max(taskStatistics.numPhoneCalls, getTaskStatisticsCallCount(taskStatistics));

export const getTaskStatisticsCallTimeSeconds = (taskStatistics: TaskStatistics) =>
  sumTaskStatisticsElapsedSeconds(getSafeTaskStatisticsPhoneCalls(taskStatistics));

export const getTaskStatisticsAverageCallSeconds = (taskStatistics: TaskStatistics) => {
  const callCount = getTaskStatisticsCallCount(taskStatistics);

  return callCount > 0 ? Math.round(getTaskStatisticsCallTimeSeconds(taskStatistics) / callCount) : 0;
};

const distributeHoldSeconds = (sessionSeconds: number[], totalHoldSeconds: number) => {
  const totalSessionSeconds = sessionSeconds.reduce((total, seconds) => total + seconds, 0);

  if (totalSessionSeconds === 0) {
    return sessionSeconds.map(() => 0);
  }

  const rawDistribution = sessionSeconds.map((seconds) =>
    Math.round((seconds / totalSessionSeconds) * totalHoldSeconds),
  );
  const delta = totalHoldSeconds - rawDistribution.reduce((total, seconds) => total + seconds, 0);

  return rawDistribution.map((seconds, index) =>
    index === rawDistribution.length - 1 ? Math.max(0, seconds + delta) : seconds,
  );
};

export const getTaskStatisticsSessionSegments = (taskStatistics: TaskStatistics): TaskStatisticsBarSegment[] => {
  const taskSessions = getSafeTaskStatisticsSessions(taskStatistics);
  const sessionSeconds = taskSessions.map((session) => getTaskStatisticsElapsedSeconds(session));
  const holdDistribution = distributeHoldSeconds(sessionSeconds, taskStatistics.timeOnHoldSeconds);

  return taskSessions.map((session, index) => ({
    accentSeconds: holdDistribution[index],
    key: session.sessionId,
    seconds: getTaskStatisticsElapsedSeconds(session),
  }));
};

export const getTaskStatisticsCallSegments = (taskStatistics: TaskStatistics): TaskStatisticsBarSegment[] =>
  getSafeTaskStatisticsPhoneCalls(taskStatistics).map((call) => ({
    key: call.callId,
    seconds: getTaskStatisticsElapsedSeconds(call),
  }));
